import { useState, useEffect } from "react";
import { toast } from 'sonner';
import "../styles/animations.css";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";

import {
  User,
  Mail,
  Phone,
  Calendar,
  Camera,
  CheckCircle,
  AlertCircle,
  Upload,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api";
import { UpdateProfileData } from "../api/profile";
import { formatMemberSinceDate } from "@/helpers/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TEAM_ROLES } from "@/helpers/collections";

export function Profile() {
  const [isLoading, setIsLoading] = useState(true);
  const { user, clientProfile, refreshUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    emailid: "",
    mobile: "",
    role: "",
    designation: "",
    avatar: "",
    timezone: "",
  });

  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
  });

  useEffect(() => {
    const loadProfileData = async () => {
      setIsLoading(true);
      try {
        if (clientProfile) {
          setProfileData({
            firstName: clientProfile.firstName || "",
            lastName: clientProfile.lastName || "",
            emailid: clientProfile.emailid || "",
            mobile: clientProfile.mobile || "",
            role: clientProfile.role || "",
            designation: clientProfile.designation || "",
            timezone: clientProfile.timezone || "",
            avatar: clientProfile.avatar || "",
          });
        }
      } catch (error) {
        toast.error("Failed to load profile data");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [clientProfile]);

  const handleEdit = () => {
    setIsEditing(true);
    setUpdateError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUpdateError(null);
    setAvatarPreview(null);
    if (clientProfile) {
      setProfileData({
        firstName: clientProfile.firstName || "",
        lastName: clientProfile.lastName || "",
        emailid: clientProfile.emailid || "",
        mobile: clientProfile.mobile || "",
        designation: clientProfile.designation || "",
        timezone: clientProfile.timezone || "",
        avatar: clientProfile.avatar || "",
        role: clientProfile.role || "",
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setUpdateError("User data not available");
      return;
    }

    const errors = {
      firstName: validateField('firstName', profileData.firstName),
      lastName: validateField('lastName', profileData.lastName),
      mobile: validateField('mobile', profileData.mobile),
    };

    setFormErrors(errors);

    if (Object.values(errors).some(error => error !== '')) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const updateData: UpdateProfileData = {
        firstname: profileData.firstName,
        lastname: profileData.lastName,
        emailid: profileData.emailid,
        mobile: profileData.mobile,
        designation: profileData.designation,
        avatar: profileData.avatar,
     
      };

      await api.profiles.updateProfile(user.id, updateData);

      await refreshUser();

      setIsUpdating(false);
      setIsEditing(false);
      setUpdateSuccess(true);

      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      setIsUpdating(false);
      setUpdateError(error instanceof Error ? error.message : "Failed to update profile");
    }
  };

  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'firstName':
        if (!value.trim()) return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        if (value.trim().length > 50) return 'First name must be less than 50 characters';
        if (!/^[a-zA-Z\s'-]+$/.test(value)) return 'First name can only contain letters, spaces, hyphens, and apostrophes';
        return '';

      case 'lastName':
        if (!value.trim()) return 'Last name is required';
        if (value.trim().length < 2) return 'Last name must be at least 2 characters';
        if (value.trim().length > 50) return 'Last name must be less than 50 characters';
        if (!/^[a-zA-Z\s'-]+$/.test(value)) return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
        return '';

      case 'mobile':
        if (value && !/^[\d\s\-\+\(\)]+$/.test(value)) return 'Mobile number can only contain digits, spaces, hyphens, and parentheses';
        if (value && value.replace(/\D/g, '').length < 10) return 'Mobile number must have at least 10 digits';
        if (value && value.replace(/\D/g, '').length > 15) return 'Mobile number must have at most 15 digits';
        return '';

      default:
        return '';
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));

    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBlur = (field: string, value: string) => {
    const error = validateField(field, value);
    setFormErrors(prev => ({ ...prev, [field]: error }));
  };

  const compressAndResizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        let { width, height } = img;
        const maxSize = 200;

        canvas.width = maxSize;
        canvas.height = maxSize;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, maxSize, maxSize);

     
        ctx.drawImage(img, 0, 0, maxSize, maxSize);

        const quality = 0.8;
        const base64String = canvas.toDataURL('image/jpeg', quality);
        resolve(base64String);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024; 
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    compressAndResizeImage(file)
      .then(compressedImage => {
        setAvatarPreview(compressedImage);

   
        fetch(compressedImage)
          .then(res => res.blob())
          .then(blob => {
            const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
            handleAvatarUpload(compressedFile);
          })
          .catch(error => {
            toast.error('Failed to process image');
          });
      })
      .catch(error => {
        toast.error('Failed to process image');
      });
  };

  const handleAvatarUpload = async (file: File) => {
    if (!user) {
      toast.error('User data not available');
      return;
    }

    setIsUploadingAvatar(true);
    setUpdateError(null);

    try {
      const response = await api.profiles.uploadAvatar(user.id, file);

      if (response.data && response.data.avatarUrl) {
        setProfileData(prev => ({
          ...prev,
          avatar: response.data.avatarUrl
        }));

        await refreshUser();

        toast.success('Avatar uploaded successfully!');
      } else {
        throw new Error('No avatar URL returned from server');
      }
    } catch (error) {
      setUpdateError(error instanceof Error ? error.message : "Failed to upload avatar");
      toast.error('Failed to upload avatar. Please try again.');
    } finally {
      setIsUploadingAvatar(false);
      setAvatarPreview(null);
    }
  };



  if (!user || !clientProfile) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 animate-fade-in">

        {updateSuccess && (
          <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 transition-all duration-300 ease-in-out animate-slide-in">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 animate-pulse" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Profile updated successfully!
            </AlertDescription>
          </Alert>
        )}

        {updateError && (
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 transition-all duration-300 ease-in-out animate-slide-in">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 animate-pulse" />
            <AlertDescription className="text-red-800 dark:text-red-200">
              {updateError}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-card shadow-sm rounded-lg border-0 transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="relative inline-block mb-4">
                  <Avatar className="w-24 h-24 mx-auto rounded-full transition-all duration-300 hover:scale-105">
                    {profileData.avatar && (
                      <AvatarImage
                        key={profileData.avatar}
                        src={import.meta.env.VITE_SOCKET_URL+profileData.avatar}
                        alt={profileData.firstName}
                        className="transition-all duration-300"
                      />
                    )}
                    <AvatarFallback className="text-lg transition-all duration-300">
                      {profileData.firstName[0]}
                      {profileData.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center avatar-upload-overlay">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                    </div>
                  )}
                  {isEditing && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute -bottom-2 -right-2 w-8 rounded-full p-0"
                      onClick={() => document.getElementById('avatar-upload')?.click()}
                      disabled={isUploadingAvatar}
                    >
                      {isUploadingAvatar ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  <input
                    id="avatar-upload"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                </div>

               
                <h2 className="text-xl font-semibold text-foreground">
                  {profileData.firstName} {profileData.lastName}
                </h2>
                <p className="text-muted-foreground mb-2">
                  {profileData.designation}
                </p>
                <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground mb-4">
                  <User className="w-4 h-4" />
                  <span>{profileData.role}</span>
                </div>
              

                <div className="flex items-center justify-center space-x-2 mt-4">
                  {!isEditing && (
                    <Button
                      onClick={handleEdit}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <Card className="bg-card shadow-sm rounded-lg border-0 transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
              <CardHeader className="pb-6">
                <div className="flex items-center space-x-2">
                  <User className="w-5 h-5 text-primary" />
                  <CardTitle>Personal Information</CardTitle>
                </div>
                <CardDescription>
                  Your basic account information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handleSave}
                  className="space-y-4 sm:space-y-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className={formErrors.firstName ? "text-destructive" : ""}>
                        First Name
                        {formErrors.firstName && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      <Input
                        id="firstName"
                        value={profileData.firstName}
                        onChange={(e) =>
                          handleInputChange(
                            "firstName",
                            e.target.value,
                          )
                        }
                        onBlur={(e) =>
                          handleBlur(
                            "firstName",
                            e.target.value,
                          )
                        }
                        disabled={!isEditing}
                        className={`h-12 rounded-lg border-border focus:border-primary focus:ring-primary disabled:bg-muted ${
                          formErrors.firstName ? "border-destructive focus:border-destructive" : ""
                        }`}
                        maxLength={50}
                        required
                      />
                      {formErrors.firstName && (
                        <p className="text-xs text-destructive mt-1">{formErrors.firstName}</p>
                      )}
                      <p className="text-xs text-muted-foreground text-right">
                        {profileData.firstName.length}/50
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName" className={formErrors.lastName ? "text-destructive" : ""}>
                        Last Name
                        {formErrors.lastName && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      <Input
                        id="lastName"
                        value={profileData.lastName}
                        onChange={(e) =>
                          handleInputChange(
                            "lastName",
                            e.target.value,
                          )
                        }
                        onBlur={(e) =>
                          handleBlur(
                            "lastName",
                            e.target.value,
                          )
                        }
                        disabled={!isEditing}
                        className={`h-12 rounded-lg border-border focus:border-primary focus:ring-primary disabled:bg-muted ${
                          formErrors.lastName ? "border-destructive focus:border-destructive" : ""
                        }`}
                        maxLength={50}
                        required
                      />
                      {formErrors.lastName && (
                        <p className="text-xs text-destructive mt-1">{formErrors.lastName}</p>
                      )}
                      <p className="text-xs text-muted-foreground text-right">
                        {profileData.lastName.length}/50
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emailid">Email Address</Label>
                    <div className="relative">
                      <Input
                        id="emailid"
                        type="email"
                        value={profileData.emailid}
                        onChange={(e) =>
                          handleInputChange(
                            "emailid",
                            e.target.value,
                          )
                        }
                        disabled={true}
                        className="h-12 rounded-lg border-border focus:border-primary focus:ring-primary disabled:bg-muted"
                        maxLength={320}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mobile" className={formErrors.mobile ? "text-destructive" : ""}>
                        Phone Number
                        {formErrors.mobile && <span className="text-destructive ml-1">*</span>}
                      </Label>
                      <div className="relative">
                       <Input
                          id="mobile"
                          type="tel"
                          value={profileData.mobile}
                          onChange={(e) =>
                            handleInputChange(
                              "mobile",
                              e.target.value,
                            )
                          }
                          onBlur={(e) =>
                            handleBlur(
                              "mobile",
                              e.target.value,
                            )
                          }
                          disabled={!isEditing}
                          className={`h-12 rounded-lg border-border focus:border-primary focus:ring-primary disabled:bg-muted ${
                            formErrors.mobile ? "border-destructive focus:border-destructive" : ""
                          }`}
                          maxLength={20}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      {formErrors.mobile && (
                        <p className="text-xs text-destructive mt-1">{formErrors.mobile}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="designation" className="text-right">
                        Designation
                      </Label>
                      <div className="relative">
                        <Select
                          value={profileData.designation}
                          disabled={!isEditing}
                          onValueChange={(value) => handleInputChange('designation', value)}>
                          <SelectTrigger size="lg" className="col-span-3">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TEAM_ROLES.map((role) => (
                              <SelectItem key={role.value} value={role.value}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                    </div>
                  </div>
                
                  {isEditing && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0 space-y-3 space-x-0 pt-4">
                      <Button
                        type="submit"
                        disabled={isUpdating}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6 w-full sm:w-auto"
                      >
                        {isUpdating
                          ? "Saving..."
                          : "Save Changes"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="rounded-lg px-6 w-full sm:w-auto"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>

            <Card className="bg-card shadow-sm rounded-lg border-0 transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
              <CardHeader className="pb-6">
                <CardTitle>Account Status</CardTitle>
                <CardDescription>
                  Your account verification and security status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
               

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>Member Since</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {formatMemberSinceDate(user.registeredon)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}